/**
 * Deal Service Layer
 * Handles all Firestore operations for the deals collection
 * Includes BUSINESS LOGIC for the THEO workflow system
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  arrayUnion,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Deal,
  DealCategory,
  DealComplexity,
  DealStage,
  JobType,
  StarlinkKitOption,
} from '../types/theo-workflow.types';
import { generateProvisioningLoadout } from './provisioning.service';
import { assertDealMayEnterStage } from './deal-stage-guard';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { logUsageEvent } from './usage-telemetry.service';

const COLLECTION_NAME = 'deals';

/**
 * Strip undefined values from an object before writing to Firestore.
 * Firestore rejects documents containing `undefined` field values.
 */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const cleaned = { ...obj };
  for (const key of Object.keys(cleaned)) {
    if (cleaned[key] === undefined) {
      delete cleaned[key];
    }
  }
  return cleaned;
}

// ============================================================================
// PROPOSAL TEMPLATE MAPPING
// ============================================================================

/**
 * Maps deal categories to Better Proposals template IDs
 * Customize these IDs based on your Better Proposals setup
 */
const PROPOSAL_TEMPLATE_MAP: Record<DealCategory, string> = {
  [DealCategory.STARLINK]: 'starlink-proposal-template',
  [DealCategory.NBN]: 'nbn-proposal-template',
  [DealCategory.SMART_HOME]: 'smart-home-proposal-template',
  [DealCategory.WIFI_EXTENSION]: 'wifi-extension-proposal-template',
  [DealCategory.SECURITY]: 'security-proposal-template',
  [DealCategory.ACCESS_CONTROL]: 'access-control-proposal-template',
  [DealCategory.AV]: 'av-proposal-template',
  [DealCategory.IT]: 'it-proposal-template',
  [DealCategory.VOIP]: 'voip-proposal-template',
  [DealCategory.FEE_FOR_SERVICE]: 'fee-for-service-proposal-template',
};

/**
 * Get the proposal template ID for a given category
 */
function getProposalTemplateForCategory(category: DealCategory): string {
  return PROPOSAL_TEMPLATE_MAP[category];
}

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new deal
 */
export async function createDeal(
  data: Omit<Deal, 'id' | 'createdAt' | 'updatedAt' | 'stage'>,
  userId?: string,
  initialStage?: DealStage
): Promise<string> {
  // Runtime enforcement of foreign-key integrity — prevents orphaned deals
  // slipping through when callers bypass the TypeScript type (e.g. `as any`).
  if (!data.customerId || data.customerId === 'manual-entry') {
    throw new Error('A valid customerId is required to create a deal');
  }

  const dealData = stripUndefined({
    ...data,
    tenantId: data.tenantId ?? getTenantIdSync(),
    stage: initialStage ?? DealStage.INTAKE,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    ...(userId && { createdBy: userId }),
  } as Record<string, unknown>) as Omit<Deal, 'id'>;

  const docRef = await addDoc(collection(db, COLLECTION_NAME), dealData);
  logUsageEvent('deal_created');
  return docRef.id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single deal by ID
 */
export async function getDeal(dealId: string): Promise<Deal | null> {
  const docRef = doc(db, COLLECTION_NAME, dealId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Deal;
}

/**
 * Get deals by customer ID
 */
export async function getDealsByCustomer(customerId: string): Promise<Deal[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Deal
  );
}

/**
 * Get deals by stage
 */
export async function getDealsByStage(stage: DealStage): Promise<Deal[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('stage', '==', stage),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Deal
  );
}

/**
 * Get deals by category
 */
export async function getDealsByCategory(category: DealCategory): Promise<Deal[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('category', '==', category),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Deal
  );
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a deal (low-level, no logging)
 */
export async function updateDeal(
  dealId: string,
  data: Partial<Omit<Deal, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, dealId);

  await updateDoc(docRef, stripUndefined({
    ...data,
    updatedAt: Timestamp.now(),
  } as Record<string, unknown>));
}

/**
 * Update a deal with activity log entry for attribution.
 * Use this for any user-initiated deal edits (field changes, notes, etc.)
 */
export async function updateDealWithLog(
  dealId: string,
  data: Partial<Omit<Deal, 'id' | 'createdAt' | 'createdBy'>>,
  userId: string,
  userName: string,
  action: string,
  details?: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, dealId);

  const logEntry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    actor: userId,
    actorName: userName,
    action,
    automated: false,
  };
  if (details !== undefined) logEntry.details = details;

  await updateDoc(docRef, stripUndefined({
    ...data,
    activityLog: arrayUnion(logEntry),
    updatedAt: Timestamp.now(),
  } as Record<string, unknown>));
}

/**
 * Move a deal to a new stage. Legacy twin of deal-workflow's
 * `moveDealToStage` (the quote-review approve path) — it passes the same
 * manual-WON guard (docs/proposal-v2/S10.md §3.3), so nothing reaches WON
 * around it.
 */
export async function moveDealToStage(
  dealId: string,
  newStage: DealStage
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, dealId);
  if (newStage === DealStage.WON) {
    const snap = await getDoc(docRef);
    assertDealMayEnterStage(snap.exists() ? (snap.data() as Deal) : null, newStage);
  }

  const updateData: Record<string, unknown> = {
    stage: newStage,
    updatedAt: Timestamp.now(),
  };

  // If moving to WON, set wonAt timestamp
  if (newStage === DealStage.WON) {
    updateData.wonAt = Timestamp.now();
  }

  // If moving to LOST, set lostAt timestamp
  if (newStage === DealStage.LOST) {
    updateData.lostAt = Timestamp.now();
  }

  await updateDoc(docRef, updateData);
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Hard-delete a deal
 * WARNING: This will orphan any provisioning items and tech requests
 */
export async function deleteDeal(dealId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, dealId);
  await deleteDoc(docRef);
}

/**
 * Soft-delete a deal.
 *
 * Sets `deleted: true` on the deal and cascades the soft-delete to all linked
 * jobs (jobs with `dealId === dealId`) and provisioning items. The deal and its
 * artefacts are retained in Firestore for audit but hidden from queries
 * that filter on `deleted != true`.
 *
 * Typically used to permanently remove abandoned, duplicate, or stale LOST /
 * COMPLETED deals from the pipeline view.
 */
export async function softDeleteDeal(
  dealId: string,
  userId: string,
  userName: string,
  reason?: string,
): Promise<void> {
  const nowIso = new Date().toISOString();
  const deletionFields = stripUndefined({
    deleted: true,
    deletedAt: nowIso,
    deletedBy: userId,
    deletedByName: userName,
    deletedReason: reason,
    updatedAt: Timestamp.now(),
  } as Record<string, unknown>);

  // Soft-delete the deal itself
  await updateDoc(doc(db, COLLECTION_NAME, dealId), deletionFields);

  // Cascade to linked jobs (fire-and-forget — don't block on failures)
  try {
    const linkedJobs = await getDocs(
      query(
        collection(db, 'jobs'),
        where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
        where('dealId', '==', dealId),
      ),
    );
    await Promise.all(
      linkedJobs.docs.map((d) =>
        updateDoc(doc(db, 'jobs', d.id), {
          deleted: true,
          deletedAt: nowIso,
          deletedBy: userId,
          deletedByName: userName,
          deletedReason: `Deal deleted: ${reason || 'no reason provided'}`,
          updatedAt: nowIso,
        }),
      ),
    );
  } catch (err) {
    console.error('[softDeleteDeal] Failed to cascade to jobs:', err);
  }

  // Cascade to provisioning items
  try {
    const linkedItems = await getDocs(
      query(
        collection(db, 'provisioning_items'),
        where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
        where('dealId', '==', dealId),
      ),
    );
    await Promise.all(
      linkedItems.docs.map((d) =>
        updateDoc(doc(db, 'provisioning_items', d.id), {
          deleted: true,
          deletedAt: nowIso,
          deletedBy: userId,
          updatedAt: nowIso,
        }),
      ),
    );
  } catch (err) {
    console.error('[softDeleteDeal] Failed to cascade to provisioning items:', err);
  }
}

/**
 * Restore a soft-deleted deal.
 *
 * Note: cascaded jobs/provisioning items are NOT automatically restored —
 * those must be restored individually if needed.
 */
export async function restoreDeal(dealId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION_NAME, dealId), {
    deleted: false,
    deletedAt: null,
    deletedBy: null,
    deletedByName: null,
    deletedReason: null,
    updatedAt: Timestamp.now(),
  });
}

// ============================================================================
// 🎯 BUSINESS LOGIC: TRIAGE (THE ROUTER)
// ============================================================================

/**
 * TRIAGE LOGIC - The Fork in the Workflow
 *
 * This is the core business logic that routes deals based on complexity:
 *
 * The Fork:
 * - IF Complexity = STANDARD → Set stage to QUOTING (auto-proposal)
 * - IF Complexity = COMPLEX → Set stage to SCOPING_REQUIRED (needs site visit)
 *
 * Also auto-assigns the correct proposal template based on category
 *
 * @param dealId - The deal to triage
 * @param category - Service category (Smart Home, NBN, Starlink, WiFi Extension, Security, IT, VoIP)
 * @param complexity - Job complexity (Standard or Complex)
 */
export async function triageDeal(
  dealId: string,
  category: DealCategory,
  complexity: DealComplexity,
  starlinkKit?: StarlinkKitOption
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, dealId);

  // THE FORK: Determine stage based on complexity
  const stage =
    complexity === DealComplexity.COMPLEX
      ? DealStage.PRESITE // Complex jobs need site visit first
      : DealStage.QUOTING; // Standard jobs can be quoted immediately

  // Auto-assign proposal template based on category
  const proposalTemplateId = getProposalTemplateForCategory(category);

  // Update the deal with triage decisions
  await updateDoc(docRef, {
    category,
    complexity,
    stage,
    proposalTemplateId,
    ...(category === DealCategory.STARLINK && starlinkKit ? { starlinkKit } : {}),
    updatedAt: Timestamp.now(),
  });

  console.log(
    `[TRIAGE] Deal ${dealId}: ${category} (${complexity}) → ${stage} | Template: ${proposalTemplateId}${starlinkKit ? ` | Kit: ${starlinkKit}` : ''}`
  );
}

/**
 * Move a deal from SCOPING_REQUIRED to QUOTING
 * (Called after a tech completes the site visit)
 */
export async function completeScopingAndMoveToQuoting(
  dealId: string
): Promise<void> {
  await moveDealToStage(dealId, DealStage.QUOTING);
  console.log(`[SCOPING COMPLETE] Deal ${dealId} moved to QUOTING`);
}

/**
 * Trigger provisioning automation when deal moves to WON
 * This function:
 * 1. Generates provisioning loadout (equipment checklist)
 * 2. Initializes job progress in the provisioning pipeline stage
 *
 * @param dealId - The deal that was won
 */
async function triggerProvisioningAutomation(dealId: string): Promise<void> {
  console.log(`[PROVISIONING AUTOMATION] Starting for deal ${dealId}`);

  // Step 1: Generate provisioning loadout
  try {
    const itemIds = await generateProvisioningLoadout(dealId);
    console.log(
      `[PROVISIONING AUTOMATION] Generated ${itemIds.length} provisioning items for deal ${dealId}`
    );
  } catch (error) {
    console.error(
      `[PROVISIONING AUTOMATION] Error generating loadout for deal ${dealId}:`,
      error
    );
    throw new Error(`Failed to generate provisioning loadout: ${error}`);
  }

  console.log(`[PROVISIONING AUTOMATION] Completed for deal ${dealId}`);
}

/**
 * Mark a deal as WON
 * This automatically triggers provisioning loadout generation
 */
export async function markDealAsWon(dealId: string): Promise<void> {
  await moveDealToStage(dealId, DealStage.WON);
  console.log(`[DEAL WON] Deal ${dealId} marked as WON`);

  // Auto-trigger provisioning automation
  try {
    await triggerProvisioningAutomation(dealId);
  } catch (error) {
    console.error(`[DEAL WON] Error triggering provisioning automation for deal ${dealId}:`, error);
    // Don't throw - deal is already marked as WON, provisioning can be triggered manually if needed
  }
}

/**
 * Mark a deal as LOST
 */
export async function markDealAsLost(dealId: string): Promise<void> {
  await moveDealToStage(dealId, DealStage.LOST);
  console.log(`[DEAL LOST] Deal ${dealId} marked as LOST`);
}

// ============================================================================
// 🎯 BUSINESS LOGIC: PRESITE WORKFLOW
// ============================================================================

/**
 * Complete a presite job and move to QUOTE_REVIEW stage
 * (Called after a tech completes the presite job with parts selection)
 *
 * @param dealId - The deal that completed presite
 */
export async function completePresiteAndMoveToQuoteReview(
  dealId: string
): Promise<void> {
  await moveDealToStage(dealId, DealStage.PROPOSAL_SENT);
  console.log(`[PRESITE COMPLETE] Deal ${dealId} moved to PROPOSAL_SENT`);
}

/**
 * Approve quote review and move to WON
 * (Called after dashboard manager approves the quote and parts)
 * This automatically triggers:
 * 1. Provisioning loadout generation
 * 2. Job progress initialization in provisioning pipeline
 *
 * @param dealId - The deal to approve
 * @param pmMeetingRequired - Whether a PM meeting is required before deployment
 */
export async function approveQuoteReviewAndMarkWon(
  dealId: string,
  pmMeetingRequired: boolean = false
): Promise<void> {
  await moveDealToStage(dealId, DealStage.WON);

  // Set PM meeting requirement flag
  const docRef = doc(db, COLLECTION_NAME, dealId);
  await updateDoc(docRef, {
    pmMeetingRequired,
    pmMeetingCompleted: false,
    updatedAt: Timestamp.now(),
  });

  console.log(`[QUOTE APPROVED] Deal ${dealId} approved and marked as WON (PM Meeting Required: ${pmMeetingRequired})`);

  // Auto-trigger provisioning automation
  try {
    await triggerProvisioningAutomation(dealId);
  } catch (error) {
    console.error(`[QUOTE APPROVED] Error triggering provisioning automation for deal ${dealId}:`, error);
    // Don't throw - deal is already marked as WON, provisioning can be triggered manually if needed
  }
}

/**
 * Complete PM Meeting
 * Marks a PM meeting as completed and returns the deal to provisioning for additional items
 *
 * @param dealId - The deal ID
 * @param notes - Optional notes from the PM meeting
 * @param userId - Optional user ID who completed the PM meeting
 */
export async function completePMeeting(
  dealId: string,
  notes?: string,
  userId?: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, dealId);

  const updateData: Record<string, unknown> = {
    pmMeetingCompleted: true,
    pmMeetingCompletedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  if (notes) {
    updateData.pmMeetingNotes = notes;
  }

  if (userId) {
    updateData.pmMeetingCompletedBy = userId;
  }

  await updateDoc(docRef, updateData);

  console.log(`[PM MEETING] PM meeting completed for deal ${dealId}`);
}

// ============================================================================
// SEED TEST DATA
// ============================================================================

/**
 * Seed test deals and customers for development/testing
 * Creates test customers and deals across different stages
 */
export async function seedTestDeals(): Promise<void> {
  console.log('[SEED] Creating test customers and deals...');

  // Create test customers first
  const testCustomers = [
    {
      name: 'John Smith',
      email: 'john.smith@example.com',
      phone: '555-0101',
      address: '123 Main St, Austin, TX 78701',
    },
    {
      name: 'Sarah Johnson',
      email: 'sarah.j@example.com',
      phone: '555-0102',
      address: '456 Oak Ave, Austin, TX 78702',
    },
    {
      name: 'Mike Williams',
      email: 'mike.w@example.com',
      phone: '555-0103',
      address: '789 Pine Rd, Austin, TX 78703',
    },
  ];

  const customerIds: string[] = [];

  for (const customerData of testCustomers) {
    const docRef = await addDoc(collection(db, 'customers'), {
      ...customerData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    customerIds.push(docRef.id);
    console.log(`[SEED] Created customer: ${customerData.name}`);
  }

  // Create test deals
  const testDeals = [
    {
      customerId: customerIds[0],
      title: 'PreSite - Smart Home Installation',
      category: DealCategory.SMART_HOME,
      complexity: DealComplexity.COMPLEX,
      jobType: JobType.PRESITE,
      stage: DealStage.PRESITE,
      jobAddress: '123 Main St, Austin, TX 78701',
      description: 'Full smart home setup - lights, locks, cameras, thermostat',
    },
    {
      customerId: customerIds[1],
      title: 'PreSite - Starlink Installation',
      category: DealCategory.STARLINK,
      complexity: DealComplexity.COMPLEX,
      jobType: JobType.PRESITE,
      stage: DealStage.PRESITE,
      jobAddress: '456 Oak Ave, Austin, TX 78702',
      description: 'Rural property Starlink setup with mesh networking',
    },
    {
      customerId: customerIds[2],
      title: 'Regular Install - Network Upgrade',
      category: DealCategory.NBN,
      complexity: DealComplexity.STANDARD,
      jobType: JobType.REGULAR,
      stage: DealStage.QUOTING,
      jobAddress: '789 Pine Rd, Austin, TX 78703',
      description: 'Upgrade existing network infrastructure',
    },
  ];

  for (const dealData of testDeals) {
    await addDoc(collection(db, COLLECTION_NAME), {
      ...dealData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    console.log(`[SEED] Created deal: ${dealData.title} (${dealData.stage})`);
  }

  console.log('[SEED] Test data seeded successfully!');
  console.log(`[SEED] Created ${customerIds.length} customers and ${testDeals.length} deals`);
}
